// CampaignViewer.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../../firebase";
import {
  FaUserCircle,
  FaBuilding,
  FaEnvelope,
  FaCalendarAlt,
  FaLock,
  FaEye,
  FaEdit,
  FaShareAlt,
  FaExclamationTriangle,
  FaCheckCircle,
} from "react-icons/fa";
import FirebaseUtils from "../services/FirebaseUtils";

const CampaignViewer = () => {
  const { ownerUid, campaignId } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [access, setAccess] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setError("You must be logged in to view this campaign");
          setLoading(false);
          return;
        }

        // First check if user has access to this campaign
        const accessInfo = await FirebaseUtils.checkCampaignAccess(
          ownerUid,
          campaignId
        );

        if (!accessInfo && ownerUid !== user.uid) {
          setError("You don't have permission to view this campaign");
          setLoading(false);
          return;
        }

        setAccess(accessInfo);

        // Fetch campaign document
        const campaignRef = doc(
          db,
          "userContent",
          ownerUid,
          "aiSdr",
          "campaigns",
          campaignId
        );
        const campaignDoc = await getDoc(campaignRef);

        if (!campaignDoc.exists()) {
          setError("Campaign not found");
          setLoading(false);
          return;
        }

        setCampaign({
          id: campaignDoc.id,
          ...campaignDoc.data(),
          ownerUid: ownerUid,
          isOwner: ownerUid === user.uid,
        });

        // Fetch campaign leads - add "collections" segment to make path valid
        const leadsQuery = query(
          collection(
            db,
            "userContent",
            ownerUid,
            "aiSdr",
            "collections", // Add this segment to make the path have odd number of segments
            "campaignLeads"
          ),
          where("campaignId", "==", campaignId)
        );

        const leadsSnapshot = await getDocs(leadsQuery);
        const campaignLeads = [];

        leadsSnapshot.forEach((doc) => {
          campaignLeads.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        setLeads(campaignLeads);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching campaign data:", err);
        setError("Failed to load campaign data. Please try again later.");
        setLoading(false);
      }
    };

    fetchCampaignData();
  }, [ownerUid, campaignId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getAccessLevelBadge = () => {
    if (!access) return null;

    switch (access.accessLevel) {
      case "read":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <FaEye className="mr-1" /> View Only
          </span>
        );
      case "edit":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaEdit className="mr-1" /> Can Edit
          </span>
        );
      case "admin":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <FaShareAlt className="mr-1" /> Admin
          </span>
        );
      case "owner":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <FaLock className="mr-1" /> Owner
          </span>
        );
      default:
        return null;
    }
  };

  const renderOverviewTab = () => {
    if (!campaign) return null;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign Details */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Campaign Details
            </h3>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Name</h4>
                <p className="text-base text-gray-900">{campaign.name}</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  Description
                </h4>
                <p className="text-base text-gray-900">
                  {campaign.description || "No description provided"}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Goal</h4>
                  <p className="text-base text-gray-900">{campaign.goal}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500">Tone</h4>
                  <p className="text-base text-gray-900">{campaign.tone}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500">Status</h4>
                  <p className="text-base text-gray-900">{campaign.status}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500">Created</h4>
                  <p className="text-base text-gray-900">
                    {formatDate(campaign.createdAt)}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500">
                  Value Proposition
                </h4>
                <p className="text-base text-gray-900">
                  {campaign.valueProposition || "No value proposition provided"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Seller Info */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Sender Information
            </h3>

            <div className="space-y-4">
              <div className="flex items-center">
                <FaUserCircle className="text-gray-400 mr-3 h-6 w-6" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Name</h4>
                  <p className="text-base text-gray-900">
                    {campaign.sellerInfo?.name || "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <FaBuilding className="text-gray-400 mr-3 h-6 w-6" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Company</h4>
                  <p className="text-base text-gray-900">
                    {campaign.sellerInfo?.company || "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <FaUserCircle className="text-gray-400 mr-3 h-6 w-6" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Role</h4>
                  <p className="text-base text-gray-900">
                    {campaign.sellerInfo?.role || "N/A"}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <FaEnvelope className="text-gray-400 mr-3 h-6 w-6" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Email</h4>
                  <p className="text-base text-gray-900">
                    {campaign.sellerInfo?.email || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sharing Info */}
          {campaign.sharedWith && (
            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Sharing Settings
              </h3>

              {campaign.sharedWith.users?.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Shared with Users
                  </h4>
                  <ul className="space-y-1">
                    {campaign.sharedWith.users.map((email) => (
                      <li
                        key={email}
                        className="text-sm text-gray-900 flex items-center"
                      >
                        <FaUserCircle className="text-gray-400 mr-2 h-4 w-4" />
                        {email}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {campaign.sharedWith.domains?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Shared with Domains
                  </h4>
                  <ul className="space-y-1">
                    {campaign.sharedWith.domains.map((domain) => (
                      <li
                        key={domain}
                        className="text-sm text-gray-900 flex items-center"
                      >
                        <FaBuilding className="text-gray-400 mr-2 h-4 w-4" />
                        {domain}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-500">
                  Permission Level
                </h4>
                <p className="text-base text-gray-900 capitalize">
                  {campaign.sharedWith.permissions}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTemplatesTab = () => {
    // Group by lead to show templates
    const leadsWithTemplates = leads.filter((lead) => lead.template);

    if (leadsWithTemplates.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <FaExclamationTriangle className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Templates Available
          </h3>
          <p className="text-gray-500">
            This campaign doesn't have any generated email templates yet.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {leadsWithTemplates.map((lead, index) => (
          <div
            key={lead.id}
            className="bg-white rounded-lg shadow overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {lead.name}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {lead.role} at {lead.company}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">{lead.email}</p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gray-50">
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-500 mb-1">
                  Subject Line
                </h4>
                <p className="text-base text-gray-900 p-3 bg-white border border-gray-200 rounded">
                  {lead.template?.subject}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">
                  Email Body
                </h4>
                <div className="p-3 bg-white border border-gray-200 rounded whitespace-pre-line">
                  {lead.template?.body}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLeadsTab = () => {
    if (leads.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <FaExclamationTriangle className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Leads Available
          </h3>
          <p className="text-gray-500">
            This campaign doesn't have any leads yet.
          </p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Role
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Company
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {leads.map((lead) => (
                <tr key={lead.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {lead.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{lead.role}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{lead.company}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{lead.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        lead.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : lead.status === "sent"
                          ? "bg-blue-100 text-blue-800"
                          : lead.status === "opened"
                          ? "bg-green-100 text-green-800"
                          : lead.status === "replied"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {lead.status || "unknown"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded my-4">
        <p>{error}</p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded my-4">
        <p>Campaign not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-800">
              {campaign.name}
            </h1>
            <div className="ml-4">{getAccessLevelBadge()}</div>
          </div>
          <p className="text-gray-500 mt-1">
            <span className="flex items-center">
              <FaCalendarAlt className="mr-1" /> Created on{" "}
              {formatDate(campaign.createdAt)}
            </span>
          </p>
        </div>

        <div className="space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>

          {(campaign.isOwner ||
            access?.accessLevel === "edit" ||
            access?.accessLevel === "admin") && (
            <button
              onClick={() =>
                navigate(`/ai-sdr/campaign-builder/${campaign.id}`)
              }
              className="px-4 py-2 bg-purple-600 rounded-md text-white hover:bg-purple-700"
            >
              Edit Campaign
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("overview")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "overview"
                ? "border-purple-500 text-purple-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Overview
          </button>

          <button
            onClick={() => setActiveTab("leads")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "leads"
                ? "border-purple-500 text-purple-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Leads ({leads.length})
          </button>

          <button
            onClick={() => setActiveTab("templates")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "templates"
                ? "border-purple-500 text-purple-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Email Templates
          </button>
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === "overview" && renderOverviewTab()}
        {activeTab === "leads" && renderLeadsTab()}
        {activeTab === "templates" && renderTemplatesTab()}
      </div>
    </div>
  );
};

export default CampaignViewer;
